/**
 * Job progress = a pure fold over the job frame stream (`subscribeJob`
 * replays persisted events on late open, so folding is order-safe). The
 * fold used to live inside the modal JobProgressPanel; refreshes now run
 * fully in the background (refreshJobsStore + the global RefreshChip), so
 * the reducer stands alone here and both the store and the chip share it.
 */

import type { JobFrame, JobPhase, JobTotals } from '../../api/types';

export interface JobProgressState {
  phase: JobPhase | null;
  planCached: number | null;
  planStale: number | null;
  nodesDone: number;
  nodesError: number;
  nodesSkipped: number;
  flowsDone: number;
  lastNodeId: string | null;
  tokensIn: number;
  tokensOut: number;
  done: { status: 'succeeded' | 'failed' | 'cancelled' | 'interrupted'; totals: JobTotals } | null;
  connectionLost: boolean;
  /**
   * How many times the job has entered the narrate phase. A prioritized
   * refresh (CANVAS2 §3.2) re-enters narrate after flows for the background
   * remainder — pass 2 means the map is already usable.
   */
  narratePasses: number;
  /** Nodes/flows completed since the CURRENT phase's job.plan (plan resets it). */
  passProgress: number;
}

/** The "map usable" milestone: the background narrate pass is running. */
export function isMapUsable(state: JobProgressState): boolean {
  return state.done === null && state.phase === 'narrate' && state.narratePasses >= 2;
}

export const initialJobProgress: JobProgressState = {
  phase: null,
  planCached: null,
  planStale: null,
  nodesDone: 0,
  nodesError: 0,
  nodesSkipped: 0,
  flowsDone: 0,
  lastNodeId: null,
  tokensIn: 0,
  tokensOut: 0,
  done: null,
  connectionLost: false,
  narratePasses: 0,
  passProgress: 0,
};

export type JobProgressAction = { kind: 'frame'; frame: JobFrame } | { kind: 'connection-error' };

/** Pure frame fold — exported for tests. */
export function jobProgressReducer(
  state: JobProgressState,
  action: JobProgressAction,
): JobProgressState {
  if (action.kind === 'connection-error') {
    return state.done === null ? { ...state, connectionLost: true } : state;
  }
  const frame = action.frame;
  // Any frame is proof the connection is back (sse.ts reports a loss only
  // once the browser has stopped retrying), whatever the frame says.
  const base = state.connectionLost ? { ...state, connectionLost: false } : state;
  switch (frame.type) {
    case 'job.phase':
      // A new phase resets the plan counters (job.plan re-emits per LLM phase).
      return {
        ...base,
        phase: frame.data.phase,
        planCached: null,
        planStale: null,
        narratePasses: base.narratePasses + (frame.data.phase === 'narrate' ? 1 : 0),
      };
    case 'job.plan':
      return {
        ...base,
        planCached: frame.data.cached,
        planStale: frame.data.stale,
        passProgress: 0,
      };
    case 'job.node':
      return {
        ...base,
        nodesDone: base.nodesDone + (frame.data.status === 'done' ? 1 : 0),
        nodesError: base.nodesError + (frame.data.status === 'error' ? 1 : 0),
        nodesSkipped: base.nodesSkipped + (frame.data.status === 'skipped' ? 1 : 0),
        lastNodeId: frame.data.node_id,
        tokensIn: base.tokensIn + frame.data.tokens_in,
        tokensOut: base.tokensOut + frame.data.tokens_out,
        passProgress: base.passProgress + 1,
      };
    case 'job.flow':
      return {
        ...base,
        flowsDone: base.flowsDone + 1,
        passProgress: base.passProgress + 1,
      };
    case 'job.done':
      return { ...base, done: frame.data };
  }
}

export const PHASE_LABELS: Record<JobPhase, string> = {
  pull: 'Pulling latest commits',
  extract: 'Reading your code (free)',
  narrate: 'Narrating changed areas',
  flows: 'Tracing flows',
};

/** Short phase word for the compact chip ("Narrating 12/74"). */
export const CHIP_PHASE_LABELS: Record<JobPhase, string> = {
  pull: 'Pulling',
  extract: 'Reading code',
  narrate: 'Narrating',
  flows: 'Tracing flows',
};

/**
 * The chip's one-line summary: live phase + counts ("Narrating 12/74"),
 * terminal state otherwise ("Map updated"). Pure — exported for tests.
 */
export function chipSummary(state: JobProgressState): string {
  if (state.done !== null) {
    return state.done.status === 'succeeded'
      ? 'Map updated'
      : state.done.status === 'cancelled'
        ? 'Refresh cancelled'
        : state.done.status === 'interrupted'
          ? 'Refresh interrupted'
          : 'Refresh failed';
  }
  if (state.phase === null) return 'Refreshing…';
  const word = CHIP_PHASE_LABELS[state.phase];
  const total = state.planStale;
  if (total !== null && total > 0 && (state.phase === 'narrate' || state.phase === 'flows')) {
    return `${word} ${Math.min(state.passProgress, total)}/${total}`;
  }
  return word;
}
